import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DialogsService, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {
  ActionState,
  CurrencySign,
  Request,
  RequestRemoteState,
  RequestState,
  RequestSubState,
  RequestWithDuplicata
} from '../../../shared/models/request';
import {ApiService} from '../../../shared/services/api.service';
import {Person} from '../../../shared/models/person';
import {formatDate} from 'src/app/shared/utils/string';
import {PlatformEvent, PlatformEventType} from '../../../shared/models/information';
import {environment} from 'src/environments/environment';
import {Document, DocumentType} from 'src/app/shared/models/user';
import {CartService, FormApplication} from '../../../shared/services/cart.service';
import {FormField, FormStep, VisaFN, VisaFormConfig, VisaFormName} from '../../../shared/models/forms';
import {format, isAfter, subHours} from 'date-fns';
import {flattenObject} from '../../../shared/utils/flattenObject';
import {RequestService} from '../../../shared/services/request.service';
import {PrintService} from '../../../shared/services/print.service';
import {DocsPrintType} from '../../../shared/models/print';
import {PlacesService} from 'src/app/shared/services/places.service';
import {showWarning, documentTypeIsVisaExtAB, documentTypeIsVisaExtC, canTrackTimeslot, enrollmentNoticeKey} from 'src/app/shared/models/default-filter-strategy';
import {DialogBookTimeSlotComponent} from 'src/app/components/dialogs/dialog-book-time-slot/dialog-book-time-slot.component';
import {documentTypeIsAnyVisa} from 'src/app/shared/models/document-type-helpers';
import {hasValue} from 'src/app/shared/utils/form';

@Component({
  selector: 'app-request-account',
  templateUrl: './request-account.component.html',
  styleUrls: ['./request-account.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RequestAccountComponent implements OnInit {
  RequestRemoteState = RequestRemoteState;
  currentRequest: RequestWithDuplicata;
  dataActualDocument: any;
  actualPerson: Person;
  PlatformEventType = PlatformEventType;
  paymentId: string;
  formatDate = formatDate;
  showWarning = showWarning;
  hasValue = hasValue;
  events: PlatformEvent[] = [];

  documentTypeIsAnyVisa = documentTypeIsAnyVisa;
  canTrackTimeslot = canTrackTimeslot;
  enrollmentNoticeKey = enrollmentNoticeKey;

  constructor(public nav: NavigateService,
    public cart: CartService,
    public api: ApiService,
    public request: RequestService,
    public places: PlacesService,
    private print: PrintService,
    private route: ActivatedRoute,
    private dialog: DialogsService,
    private loader: LoaderService,
    private lang: LangService) {
    this.paymentId = this.route.snapshot.paramMap.get('paymentId');
  }

  ngOnInit() {
    const action = () => {
      if(this.currentRequest) {
        if(typeof this.currentRequest.serialized === 'string' && this.currentRequest.serialized && this.currentRequest.id) {
          this.loader.loading(true);
          this.currentRequest.serialized = this.api.getMetadataPayment(this.currentRequest.serialized, this.currentRequest.id).then((res: any) => {
            this.currentRequest.serialized = res;
            this.getExtensionsMetadata();
            this.loader.loading(false);
            return res;
          }).catch(() => {
            this.loader.loading(false);
            this.getExtensionsMetadata();
            return this.currentRequest.serialized;
          });
        }
      } else {
        this.nav.to('trips');
      }
    };

    const application = this.request.allMyApplications?.find((app: {batchId: string, requests: Request[] | RequestWithDuplicata[]}) => app.requests.find((request: Request | RequestWithDuplicata) => request.id === this.paymentId));
    if(!application) return this.nav.to('trips');
    this.currentRequest = application.requests.find(request => request.id === this.paymentId);

    action();
  }

  startProcess(isResume: boolean, request: RequestWithDuplicata = this.currentRequest) {
    const visaFormConfig: VisaFormConfig = JSON.parse(JSON.stringify(this.cart.getOriginalForm(request.operationId as DocumentType)));
    const meta = flattenObject(request.serialized);

    if(meta.NextOfKin) {
      meta.NextOfKin = JSON.parse(meta.NextOfKin);
      meta[VisaFN.EMERGENCY_LAST_NAME] = meta.NextOfKin.lastname;
      meta[VisaFN.EMERGENCY_FIRST_NAME] = meta.NextOfKin.firstname;
      meta[VisaFN.EMERGENCY_EMAIL] = meta.NextOfKin.email;
      meta[VisaFN.EMERGENCY_PHONE] = meta.NextOfKin.phone;
    }

    Object.keys(meta).forEach((key: any) => {
      Object.keys(visaFormConfig).forEach((step: string) => {
        const formStep: FormStep = visaFormConfig[step];
        formStep.errors = formStep.errors || [];
        const formField: FormField = formStep.fields?.find((field: FormField) => field.name === key);

        if(formField) {
          const val: any = meta[key];
          if(isResume || !request.refusalRefusedFields?.includes(key)) {
            switch(formField.type) {
            case 'date':
              formField.value = format(new Date(val), 'yyyy-MM-dd');
              break;
            case 'checkbox':
              formField.value = (val.toLowerCase() === 'true' || val.toLowerCase() === 'yes') ? val : '';
              break;
            default:
              formField.value = val;
              break;
            }
            if(!isResume) formField.disabled = true;
          } else if(!isResume && request.refusalRefusedFields?.includes(key)) {
            formStep.errors.push({key: key, keyError: 'refused'});
            formStep.hasRevisionErrors = true;
          }
        }

        if(!isResume) {
          formStep.visited = true;
          formStep.submitted = true;
        }

        if(step === VisaFormName.MISCELLANEOUS_DETAILS && !this.api.env.useGuichet) {
          formStep.fields.find((field: FormField) => field.name === VisaFN.BATCH_ID).value = request.batchId;
        }
      });
    });

    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_COUNTRY, visaFormConfig)) {
      const placeOfBirth = meta.PlaceOfBirth ? meta.PlaceOfBirth.split(' - ') : ['', ''];
      const countryField = this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_COUNTRY, visaFormConfig);
      const cityField = this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_CITY, visaFormConfig);
      const placeOfBirthField = this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH, visaFormConfig);

      if(countryField && !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH_COUNTRY)) {
        countryField.value = placeOfBirth[0].trim();
      }
      if(cityField && !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH_CITY)) {
        cityField.value = placeOfBirth[1].trim();
      }
      if(placeOfBirthField && !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH)) {
        placeOfBirthField.value = meta.PlaceOfBirth || '';
      }

      if(!isResume) {
        if(countryField) countryField.disabled = !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH_COUNTRY);
        if(cityField) cityField.disabled = !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH_CITY);
        if(placeOfBirthField) placeOfBirthField.disabled = !request.refusalRefusedFields?.includes(VisaFN.PLACE_OF_BIRTH);
      }
    }

    if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, visaFormConfig)) {
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, visaFormConfig).value = meta.PreviousConvictionsZim ? 'true' : 'false';
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM, visaFormConfig).value = isResume || !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_ZIM) ? meta.PreviousConvictionsZim : '';
      if(!isResume) {
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_ZIM);
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_ZIM);
      }
    }

    if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ASK, visaFormConfig)) {
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ASK, visaFormConfig).value = meta.PreviousConvictions ? 'true' : 'false';
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS, visaFormConfig).value = isResume || !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS) ? meta.PreviousConvictions : '';
      if(!isResume) {
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ASK, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS);
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS);
      }
    }

    if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, visaFormConfig)) {
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, visaFormConfig).value = meta.PreviousConvictionsHome ? 'true' : 'false';
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME, visaFormConfig).value = isResume || !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_HOME) ? meta.PreviousConvictionsHome : '';
      if(!isResume) {
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_HOME);
        this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME, visaFormConfig).disabled = !request.refusalRefusedFields?.includes(VisaFN.PREVIOUS_CONVICTIONS_HOME);
      }
    }

    if(!isResume && request.refusalRefusedFields?.includes(VisaFN.PURPOSE_OF_VISIT)) {
      visaFormConfig[VisaFormName.DOCUMENT].hasRevisionErrors = true;
      visaFormConfig[VisaFormName.DOCUMENT].fields.forEach((field: FormField) => {
        field.value = undefined;
        field.disabled = false;
        field.hidden = true;
      });
    }

    const application: FormApplication = {
      travelName: request.batchId,
      plannedEntry: meta.ProvisionnalEntryPort,
      bookingDate: request.bookingDate,
      requests: [
        {
          formConfig: visaFormConfig,
          nationality: isResume || !(request.refusalRefusedFields as VisaFN[])?.includes(VisaFN.NATIONALITY) ? meta.Nationality : undefined,
          passportType: isResume || !(request.refusalRefusedFields as VisaFN[])?.includes(VisaFN.PASSPORT_TYPE) ? meta.PassportType : undefined,
          visaType: isResume || !(request.refusalRefusedFields)?.includes(VisaFN.VISA_TYPE) ? this.api.userInfo.server.paymentRequests.find((doc: Document) => doc.id === request.operationId) : undefined,
          urgent: meta.Urgent,
          person: {
            firstName: this.api.listPersons.find((person: Person) => person.id === request.person_id)?.firstName || '',
            lastName: this.api.listPersons.find((person: Person) => person.id === request.person_id)?.lastName || '',
            nationalNumber: '',
            batchIds: [],
            passportNumber: '',
            id: Number(request.person_id),
            linkType: null,
            otherLinkType: '',
            birthDate: '',
            sex: request.metadata?.Gender,
            birthPlaceCity: '',
            birthPlaceCountry: ''
          }
        }
      ],
      visaFeesAndTerms: false,
      requestId: request.id,
      isRevision: !isResume,
      isExtend: documentTypeIsVisaExtAB(request.operationId) || documentTypeIsVisaExtC(request.operationId),
      revisionFields: request.refusalRefusedFields as VisaFN[],
      visaReference: meta.VisaReference
    };

    if(isResume) {
      return this.request.pay(true, application).catch((err) => {
        this.loader.loading(true, {type: 'error', message: err});
      });
    } else {
      this.loader.loading(true, {type: 'info', message: 'revision.start', btnLabel: 'yes', custom: {closeBtnLabel: 'no'}}).then((done) => {
        if(done) {
          this.cart.startApplication(false, application, visaFormConfig);
        }
      });
    }
  }

  printApprovalLetter() {
    const baseHref = this.environment.baseHref === '' ? '/' : this.environment.baseHref + 'index.html';
    this.currentRequest.qrCodeVerification = window.location.origin + baseHref + '#/qr/' + this.paymentId + '?from=ScanQR';
    this.print.print<RequestWithDuplicata>(DocsPrintType.APPROVAL_LETTER, this.currentRequest);
  }

  calculateDeadlineDate(usageAfter: string, millis: number): Date {
    const usageAfterDate = new Date(usageAfter);
    const deadlineDate = new Date(usageAfterDate.getTime() + millis);
    return deadlineDate;
  }

  getAmount(currency: keyof typeof CurrencySign, ammount: number, millis = true, showCurrency: boolean = true) : string{
    return (showCurrency && CurrencySign[currency]) + (millis ? ammount / 100 : ammount);
  }

  getExtensionsMetadata() {
    if(!this.currentRequest?.extensions?.length) {
      return;
    } else {
      this.currentRequest.extensions.map((request: RequestWithDuplicata | Request) => {
        if(typeof request?.serialized === 'string') {
          return this.api.getMetadataPayment(request.serialized, request?.id).then((res: any) => {
            request.serialized = res;
            return request;
          }).catch(() => request);
        }
        return request;
      });
    }
  }

  isExtend() {
    return this.currentRequest && (documentTypeIsVisaExtAB(this.currentRequest.operationId) || documentTypeIsVisaExtC(this.currentRequest.operationId));
  }

  cancelDraft() {
    return this.loader.loading(true, {type: 'warn', btnLabel: 'yes', custom: {closeBtnLabel: 'no', innerHtml: this.lang.transform('cancel.draft')}}).then((done) => {
      if(done) {
        this.loader.loading(true);
        return this.api.cancelPayment(this.currentRequest.id, 'Customer manual cancel button').then(() => this.loader.loading(true, {type: 'valid', message: 'draft.cancelled'}).then(() => {
          this.request.cleanupDraft(this.currentRequest.id);
          return this.nav.to('trips');
        })).catch(() => this.loader.loading(true, {type: 'error', message: 'draft.cancelled.error'}));
      }
    });
  }

  changeTimeSlot() {
    const placeId = this.currentRequest?.timeslot_place_id ?? this.currentRequest?.indexed?.RegistrationPortId;
    const minDistanceHours = this.api.userInfo.server.paymentTimeSlotMinDistanceHours;
    const meetingDate = new Date(this.currentRequest?.timeslot);
    const now = new Date(new Date());
    const deadline = subHours(meetingDate, minDistanceHours);

    if(this.currentRequest?.timeslot){
      if(isAfter(now, deadline)) {
        this.loader.loading(true, {type: 'warn', message: this.lang.transform('timeslot.tooCloseToChange', {hours: minDistanceHours})});
        return;
      }
    }

    const usageUntilDate = this.currentRequest?.usageUntil || '';
    this.dialog.openDialog(DialogBookTimeSlotComponent, 'lg', {payment: this.currentRequest, placeId: placeId, usageUntil: usageUntilDate, isChange: true}, true, undefined, true).then(() => {
      window.location.reload();
    });
  }

  getSlotEndTime(timeSlot: string): Date {
    const start = new Date(timeSlot);
    return new Date(start.getTime() + this.api.userInfo.server.paymentTimeSlotDuration);
  }

  protected readonly environment = environment;
  protected readonly DocumentType = DocumentType;
  protected readonly RequestSubState = RequestSubState;
  protected readonly ActionState = ActionState;
  protected readonly RequestState = RequestState;
}
